import posthog from "posthog-js";

import { aoMudarConsentimento, temConsentimentoAnalitico } from "utils/consent";

/**
 * Dono do ciclo de vida do PostHog.
 *
 * O PostHog aqui tem dois modos, e a diferença entre eles é o que sustenta a
 * base legal de cada um:
 *
 * - **Sem consentimento** (padrão): só monitoramento de erro. Sem identificador
 *   persistente (`persistence: "memory"` — nada de cookie ou localStorage), sem
 *   perfil de pessoa, sem autocapture, sem pageview. Isso é telemetria
 *   operacional, sustentada por legítimo interesse: serve para manter o serviço
 *   de pé, não para acompanhar alguém. O `before_send` descarta qualquer evento
 *   que não seja `$exception`, então mesmo que alguma captura escape da config
 *   ela não sai daqui.
 * - **Com consentimento**: analytics completo, como era antes — pageview,
 *   autocapture, perfil de pessoa e identificação por uid.
 *
 * A troca entre os modos é feita com `set_config` em tempo de execução, e a
 * revogação passa por `reset()` para apagar o identificador já gravado.
 */

const CHAVE = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

const isLocalhost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

/** Só sobe em produção, fora de localhost e com a chave configurada. */
export const posthogHabilitado =
  Boolean(import.meta.env.PROD) && !isLocalhost && Boolean(CHAVE);

let comConsentimento = temConsentimentoAnalitico();
// Guardado só em memória: permite reidentificar se o usuário aceitar os cookies
// no meio da sessão, sem depender de um novo evento de auth.
let usuarioAtual = null;

/** Remove query string e hash, preservando origem e caminho. */
const limparUrl = (valor) => {
  if (typeof valor !== "string") return valor;
  try {
    const url = new URL(valor);
    return `${url.origin}${url.pathname}`;
  } catch {
    return valor;
  }
};

const CHAVES_DE_URL = [
  "$current_url",
  "$referrer",
  "$initial_current_url",
  "$initial_referrer",
  "$pathname",
];

/**
 * Exceção que nasceu de script injetado pelo navegador, não do app — ex.: o
 * autofill do XBrowser (Android) chama `execute_auto_fill`, que referencia a
 * ponte `xbrowser` e explode com ReferenceError quando ela não existe. Todo
 * código do app é servido por http(s), então uma pilha sem nenhum frame vindo
 * de lá não tem nada para corrigirmos. Sem pilha alguma (ex.: promise rejeitada
 * com string) não dá para saber a origem, e o evento passa.
 */
const ehExcecaoDeTerceiro = (evento) => {
  const frames = (evento.properties?.$exception_list ?? []).flatMap(
    (excecao) => excecao?.stacktrace?.frames ?? [],
  );
  if (frames.length === 0) return false;
  return !frames.some((frame) => /^https?:\/\//.test(frame?.filename ?? ""));
};

const sanitizar = (evento) => {
  if (!evento) return evento;

  if (evento.event === "$exception" && ehExcecaoDeTerceiro(evento)) return null;

  // A query string carrega o `oobCode` do Firebase (reset de senha, verificação
  // de e-mail) e afins. Nada disso ajuda a depurar e não pode sair do navegador,
  // então cai fora nos dois modos.
  if (evento.properties) {
    CHAVES_DE_URL.forEach((chave) => {
      if (evento.properties[chave]) {
        evento.properties[chave] = limparUrl(evento.properties[chave]);
      }
    });
  }

  if (comConsentimento) return evento;

  if (evento.event !== "$exception") return null;

  // `$ip: null` pede ao PostHog para descartar o IP deste evento. O interruptor
  // definitivo é "Discard client IP data" nas configurações do projeto, que só
  // pode ser ligado no painel.
  evento.properties = { ...evento.properties, $ip: null };
  return evento;
};

/** Modo restrito: só erro, sem nada que permita seguir a pessoa. */
const CONFIG_MINIMA = {
  persistence: "memory",
  person_profiles: "never",
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  disable_session_recording: true,
  disable_surveys: true,
};

/**
 * Modo completo: o comportamento que já existia para quem aceita os cookies.
 * Cada chave do modo restrito precisa reaparecer aqui com o valor padrão do
 * PostHog — `set_config` só sobrescreve o que recebe, e o que ficasse de fora
 * continuaria valendo com o valor restrito.
 */
const CONFIG_COMPLETA = {
  persistence: "localStorage+cookie",
  person_profiles: "identified_only",
  autocapture: true,
  capture_pageview: true,
  capture_pageleave: "if_capture_pageview",
  disable_session_recording: false,
  disable_surveys: false,
};

const aplicarModo = (aceito) => {
  comConsentimento = aceito;
  if (!posthog.__loaded) return;

  if (aceito) {
    posthog.set_config(CONFIG_COMPLETA);
    if (usuarioAtual?.uid) {
      posthog.identify(usuarioAtual.uid, {
        email: usuarioAtual.email,
        role: usuarioAtual.role,
      });
    }
    return;
  }

  // Revogação: apaga o distinct_id e as propriedades já persistidas antes de
  // trocar para memória, senão o cookie/localStorage sobreviveria à mudança.
  posthog.reset();
  posthog.set_config(CONFIG_MINIMA);
};

if (posthogHabilitado) {
  posthog.init(CHAVE, {
    api_host: HOST,
    debug: false,
    // Instala os handlers globais de window.onerror e unhandledrejection — é o
    // que pega erro não tratado, inclusive promise rejeitada no boot.
    capture_exceptions: true,
    before_send: sanitizar,
    ...(comConsentimento ? CONFIG_COMPLETA : CONFIG_MINIMA),
  });
}

aoMudarConsentimento(aplicarModo);

/**
 * Reporta um erro já tratado (os `logger.error`/`logger.warn` do app), que por
 * definição não passa pelos handlers globais.
 */
export const capturarErro = (erro, contexto) => {
  if (!posthog.__loaded) return;
  try {
    if (erro instanceof Error) {
      posthog.captureException(erro, contexto);
    } else {
      posthog.captureException(new Error(String(erro)), contexto);
    }
  } catch {
    // Observabilidade nunca deve quebrar o fluxo do app.
  }
};

/**
 * Associa (ou dissocia) o usuário atual aos eventos. Identificar cria um perfil
 * persistente, então só acontece com consentimento — sem ele, o uid fica apenas
 * em memória, à espera de uma eventual aceitação.
 */
export const identificarUsuario = (u) => {
  usuarioAtual = u?.uid ? u : null;
  if (!posthog.__loaded) return;
  try {
    if (usuarioAtual && comConsentimento) {
      posthog.identify(usuarioAtual.uid, {
        email: usuarioAtual.email,
        role: usuarioAtual.role,
      });
    } else if (!usuarioAtual) {
      posthog.reset();
    }
  } catch {
    // Observabilidade nunca deve quebrar o fluxo de auth.
  }
};

export default posthog;
